import { isSelfHosted } from "@/helpers/self-hosted.helper"
import { useTranslation } from "@/hooks/useTranslation"
import {
  DeployTab,
  TitleStepDeployTab,
  type CommonDeployTabProps
} from "@/pages/DeploymentsPage/components/molecules/DeployTab"
import { CommonDeployCommand } from "@/pages/DeploymentsPage/components/organisms/CommonDeployCommand"
import { DeploymentAddHelmChartCommand } from "@/pages/DeploymentsPage/components/organisms/DeploymentAddHelmChartCommand"
import { DownloadStep } from "@/pages/DeploymentsPage/components/organisms/DownloadStep"
import { getCommonFiles } from "@/pages/DeploymentsPage/helpers"

const getDeploymentStepTitle = (
  hasDeploymentFile: boolean,
  hasSecretFile: boolean,
  t: (key: string) => string
): string => {
  if (hasDeploymentFile && hasSecretFile) {
    return t("deployments.kubernetes.deploymentStep")
  } else if (hasDeploymentFile && !hasSecretFile) {
    return t("deployments.kubernetes.deploymentStepNoRegistrySecret")
  } else {
    return t(
      "deployments.kubernetes.deploymentStepNoRegistrySecretNoDeployment"
    )
  }
}

export const CommonDeployTab = (props: CommonDeployTabProps) => {
  const { noError, version } = props
  const files = getCommonFiles(version)
  const { t } = useTranslation()

  const hasDeploymentFile = files.some((file) => file.fileType === "deployment")
  const hasSecretFile = files.some((file) => file.fileType === "secret")

  const deployTabTitlePrefix = hasDeploymentFile
    ? t("general.download")
    : t("general.obtain")
  const deployTabTitle = getDeploymentStepTitle(
    hasDeploymentFile,
    hasSecretFile,
    t
  )

  return (
    <DeployTab
      steps={[
        {
          title: (
            <DownloadStep
              noError={noError}
              files={files}
              titlePrefix={deployTabTitlePrefix}
              title={deployTabTitle}
            />
          ),
          status: "process"
        },
        ...(!hasDeploymentFile
          ? [
              {
                title: (
                  <TitleStepDeployTab
                    prefix={t("deployments.obtainKodemHelmChart")}
                  >
                    {`- ${t("deployments.modifyAndRunCommands")}`}
                  </TitleStepDeployTab>
                ),
                description: <DeploymentAddHelmChartCommand {...props} />,
                status: "process" as const
              }
            ]
          : []),
        {
          title: (
            <TitleStepDeployTab prefix={t("general.deploy")}>
              {isSelfHosted()
                ? `- ${t("deployments.kubernetes.commandStepSelfHosted")}`
                : `- ${t("deployments.kubernetes.commandStep")}`}
            </TitleStepDeployTab>
          ),
          description: (
            <CommonDeployCommand
              {...props}
              hasDeploymentFile={hasDeploymentFile}
            />
          ),
          status: "process"
        }
      ]}
      message={
        <span>
          {t("deployments.kubernetes.alert.text")}
          <br />
          <b>{t("deployments.kubernetes.alert.note")}</b>
        </span>
      }
    />
  )
}
